import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { useI18n } from "i18n";
import {
  selectOngoingMyEvents,
  selectPastMyEvents,
  selectUpcomingMyEvents,
} from "modules/event/selectors";
import { useSelector } from "react-redux";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import useCompanyEventDirectory from "modules/eventDirectory/hooks/useCompanyEventDirectory";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { IEvent } from "modules/event/types";
import { IEventDirectory } from "modules/eventDirectory/types";
import { UserFeatures } from "services/splitService/features";
import { useUserSplitTreatment } from "modules/split";
import { useStyles } from "./index.styles";
import DialogHeader from "./DialogHeader";
import DialogFooter from "./DialogFooter";
import PreviewPanel from "./PreviewPanel";
import { EventList } from "../EventList";

interface Props {
  directory?: IEventDirectory;
  handleClose: () => void;
}

export type EventOption = IEvent | null;

const EventDirectoryDialog = ({ handleClose, directory }: Props) => {
  const styles = useStyles();
  const { t } = useI18n(["common", "eventDirectory"]);
  const { createCompanyEventDirectory, updateCompanyEventDirectory } =
    useCompanyEventDirectory();
  const { featureEnabled } = useUserSplitTreatment(
    UserFeatures.MULTIPLE_EVENT_DIRECTORIES,
  );
  const ongoingEvents = useSelector(selectOngoingMyEvents);
  const upcomingEvents = useSelector(selectUpcomingMyEvents);
  const pastEvents = useSelector(selectPastMyEvents);
  const eventOptions = [...ongoingEvents, ...upcomingEvents].map((event) => {
    const newEvent = { ...event };
    if (!event?.directoryId) {
      return newEvent;
    }
    // @ts-expect-error type error
    const {
      directoryId,
      directories,
    }: {
      directoryId: IEventDirectory;
      directories: IEventDirectory[];
    } = event;
    if (directoryId?.id) {
      if (!directories?.length) {
        newEvent.directories = [directoryId];
      } else if (
        !directories?.some(
          (eventDirectory) => eventDirectory?.id === directoryId?.id,
        )
      ) {
        newEvent.directories = [...directories, directoryId];
      }
    }
    return newEvent;
  });
  const { addWarningNotification } = useNotificationActions();
  const { getDirectoryEventLimit } = useCompanyPlanSettings();
  const [eventArray, setEventArray] = useState<EventOption[]>([null, null]);
  const [directoryName, setDirectoryName] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  const maxEvents = getDirectoryEventLimit();
  const minEvents = 2;

  const reducedEventArray = useMemo(
    () =>
      eventArray.reduce(
        (prev, curr) => (curr ? [...prev, curr.id] : prev),
        [] as string[],
      ),
    [eventArray],
  );

  useEffect(() => {
    if (directory) {
      const populatedArray = directory.events
        .map(
          (event) =>
            [...eventOptions, ...pastEvents].find(
              (existingEvent) => existingEvent.id === (event as IEvent).id,
            ) ?? null,
        )
        .filter((events) => events !== null);

      setDirectoryName(directory.name);
      setEventArray(populatedArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directory]);

  const handleDirectoryNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setDirectoryName(event.target.value),
    [],
  );

  const handleSubmit = async () => {
    setTouched(true);

    if (!directoryName.length || reducedEventArray.length < 2) {
      addWarningNotification({
        message: t("eventDirectory:complete.all.fields"),
      });

      return;
    }

    setLoading(true);
    const data = {
      name: directoryName,
      events: reducedEventArray,
    };

    const isSuccess = directory
      ? await updateCompanyEventDirectory(directory.id, data)
      : await createCompanyEventDirectory(data);

    if (isSuccess) {
      handleClose();
    }
    setLoading(false);
  };

  return (
    <Dialog fullWidth maxWidth="lg" open id="event-directory-dialog">
      <DialogHeader handleClose={handleClose} isEditMode={!!directory} />
      <DialogContent className={styles.content}>
        <Container className={styles.contentLeft}>
          <Input
            name="directory-name"
            fullWidth
            size="sm"
            label={t("eventDirectory:directory.name")}
            placeholder={t("eventDirectory:name.your.directory")}
            value={directoryName}
            defaultValue={directoryName}
            onChange={handleDirectoryNameChange}
            error={
              touched && !directoryName.length
                ? `${t("eventDirectory:name.required")}`
                : false
            }
          />
          <Typography variant="h5" className={styles.draggableLabel}>
            {t("eventDirectory:link.events")}
            {` ${
              eventArray.filter((events) => events !== null).length
            }/${maxEvents}`}
          </Typography>
          {!featureEnabled && (
            <Typography
              variant="subtitle1"
              className={styles.draggableLabelSubtitle}
            >
              {t("eventDirectory:private.events.not.supported")}
            </Typography>
          )}
          <EventList
            eventArray={eventArray}
            reducedEventArray={reducedEventArray}
            options={eventOptions}
            setEventArray={setEventArray}
            touched={touched}
            minEvents={minEvents}
            maxEvents={maxEvents}
            directory={directory}
          />
        </Container>
        <PreviewPanel name={directoryName} eventArray={eventArray} />
      </DialogContent>
      <DialogFooter
        isEditMode={!!directory}
        handleClose={handleClose}
        handleSubmit={handleSubmit}
        loading={loading}
      />
      {loading && (
        <PageLoader
          message={
            directory
              ? t("eventDirectory:updating.event.directory")
              : t("eventDirectory:creating.event.directory")
          }
        />
      )}
    </Dialog>
  );
};

export default EventDirectoryDialog;
